import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      // Set the auth header BEFORE fetching user
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      // Ensure headers are cleared when token is null
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }
  }, [token, fetchCurrentUser]);

  const login = async (email, password) => {
    try {
      console.log('Login attempt:', { email });
      
      // CRITICAL: Clear ALL session data before login to prevent cross-user contamination
      localStorage.clear();
      sessionStorage.clear();
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      
      // Small delay to ensure React state flush before new login
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Fresh login request with no cached auth headers
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      const { access_token, user: userData } = response.data;
      
      // Verify the response matches the requested email
      if (userData.email.toLowerCase() !== email.toLowerCase()) {
        console.error('AUTH MISMATCH: Requested', email, 'but got', userData.email);
        return { success: false, error: 'Authentication mismatch. Please try again.' };
      }
      
      console.log('Login success:', { userId: userData.id, email: userData.email });
      
      // Set new session
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error.response?.data?.detail || error.message);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/signup`, userData);
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(newUser);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Signup failed' 
      };
    }
  };

  const value = {
    user,
    token,
    login,
    signup,
    logout,
    loading,
    setUser  // Expose setUser for profile updates
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
